<template>
  <div class="mt-3 pt-3 border-t border-amber-200">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-2 mb-2 text-left"
      @click="expanded = !expanded"
    >
      <h4 class="font-bold text-amber-800 text-sm">
        Commentaires admin<span v-if="comments.length"> ({{ comments.length }})</span>
      </h4>
      <i
        :class="expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
        class="text-amber-700 text-xs"
      ></i>
    </button>

    <div v-show="expanded">
    <p v-if="loading" class="text-xs text-gray-500">Chargement...</p>

    <ul
      v-if="comments.length"
      class="space-y-2 mb-3 max-h-52 overflow-y-auto overscroll-contain"
      @wheel.stop
    >
      <li
        v-for="item in comments"
        :key="item.id_acheteur_commentaire"
        class="rounded border border-amber-100 bg-amber-50 px-2 py-1.5"
      >
        <div class="flex items-start justify-between gap-2">
          <p class="text-xs text-gray-700">
            <span class="font-semibold text-black">{{ authorName(item) }}</span>
            <span class="text-gray-500"> · {{ formatDateTime(item.date_creation) }}</span>
          </p>
          <button
            v-if="item.canDelete"
            type="button"
            class="text-red-500 hover:text-red-700"
            title="Supprimer le commentaire"
            :disabled="saving"
            @click="remove(item)"
          >
            <i class="fas fa-trash-alt text-xs"></i>
          </button>
        </div>
        <p class="text-sm text-black whitespace-pre-wrap">{{ item.commentaire }}</p>
      </li>
    </ul>
    <p v-else-if="!loading" class="text-xs text-gray-500 mb-2">
      Aucun commentaire pour cet acheteur.
    </p>

    <label class="mb-1 block text-xs font-semibold text-gray-700">
      Ajouter un commentaire
    </label>
    <textarea
      v-model="draft"
      rows="2"
      maxlength="256"
      placeholder="Écrivez votre commentaire..."
      class="w-full resize-none rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
    ></textarea>
    <div class="mt-1 flex items-center justify-between gap-2">
      <span class="text-[11px] text-gray-400">{{ draft.trim().length }}/256</span>
      <button
        type="button"
        class="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="saving || !draft.trim()"
        @click="save"
      >
        Enregistrer
      </button>
    </div>
    </div>

    <ConfirmModal
      :show="pendingDelete != null"
      title="Supprimer le commentaire"
      message="Supprimer ce commentaire ?"
      confirm-text="Supprimer"
      confirm-button-class="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      :confirm-loading="saving"
      loading-text="Suppression…"
      @confirm="confirmRemove"
      @cancel="pendingDelete = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { fetchRest, getApiBaseUrl } from "@/config/api";
import { ConfirmModal } from "@/components";
import { useNotification } from "@/composables/useNotification";

interface BuyerComment {
  id_acheteur_commentaire: number;
  id_acheteur: number;
  id_adminuser: number;
  commentaire: string;
  date_creation: string;
  prenom?: string | null;
  nom?: string | null;
  canDelete?: boolean;
}

const props = defineProps<{
  idAcheteur: number;
}>();

const { success, error: notifyError } = useNotification();
const comments = ref<BuyerComment[]>([]);
const expanded = ref(false);
const draft = ref("");
const loading = ref(false);
const saving = ref(false);
const pendingDelete = ref<BuyerComment | null>(null);

const authorName = (item: BuyerComment) => {
  const name = `${item.prenom || ""} ${item.nom || ""}`.trim();
  return name || "Admin";
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const load = async () => {
  if (!props.idAcheteur) {
    comments.value = [];
    return;
  }
  loading.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/get`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idAcheteur: props.idAcheteur }),
      },
    );
    const data = await response.json();
    comments.value = Array.isArray(data) ? data : [];
  } catch {
    notifyError("Impossible de charger les commentaires.");
    comments.value = [];
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  const commentaire = draft.value.trim();
  if (!commentaire || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/save`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idAcheteur: props.idAcheteur, commentaire }),
      },
    );
    const created = await response.json();
    if (created?.id_acheteur_commentaire) {
      comments.value = [...comments.value, created];
      draft.value = "";
      success("Commentaire enregistré.");
    } else {
      notifyError("Le commentaire n'a pas été enregistré.");
    }
  } catch {
    notifyError("Impossible d'enregistrer le commentaire.");
  } finally {
    saving.value = false;
  }
};

const remove = (item: BuyerComment) => {
  if (!item.canDelete || saving.value) return;
  pendingDelete.value = item;
};

const confirmRemove = async () => {
  const item = pendingDelete.value;
  if (!item || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/acheteurs/commentaires/delete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idAcheteur: props.idAcheteur,
          idCommentaire: item.id_acheteur_commentaire,
        }),
      },
    );
    const deleted = await response.json();
    if (deleted === true) {
      comments.value = comments.value.filter(
        (comment) =>
          comment.id_acheteur_commentaire !== item.id_acheteur_commentaire,
      );
      pendingDelete.value = null;
      success("Commentaire supprimé.");
    } else {
      notifyError("Ce commentaire ne peut pas être supprimé.");
      pendingDelete.value = null;
    }
  } catch {
    notifyError("Impossible de supprimer le commentaire.");
    pendingDelete.value = null;
  } finally {
    saving.value = false;
  }
};

watch(() => props.idAcheteur, () => void load(), { immediate: true });
</script>
